import { PAGE_SIZES } from "../lib/paging.ts";
import { fmt, useT } from "../i18n/index.tsx";
import { IconChevron } from "./Icons.tsx";

const num = (n: number) => n.toLocaleString("en");

/** 1 … 4 5 6 … 25 */
function pageList(page: number, pages: number): (number | "…")[] {
  const out: (number | "…")[] = [];
  const add = (n: number | "…") => out[out.length - 1] !== n && out.push(n);
  for (let i = 1; i <= pages; i++) {
    if (i === 1 || i === pages || Math.abs(i - page) <= 1) add(i);
    else if (out[out.length - 1] !== "…") add("…");
  }
  return out;
}

type Props = {
  page: number;
  limit: number;
  /** Items on this page. */
  count: number;
  /** Total rows (from the server); without it the pager only offers Prev / Next. */
  total?: number;
  hasMore?: boolean;
  onPage: (p: number) => void;
  onLimit: (l: number) => void;
  label?: string;
};

/** "1–50 of 1,234" · Prev 1 2 3 … 25 Next · rows per page. Same for every table. */
export function Pager({ page, limit, count, total, hasMore, onPage, onLimit, label }: Props) {
  const c = useT().common;
  const pages = total !== undefined ? Math.max(1, Math.ceil(total / limit)) : page + (hasMore ? 1 : 0);
  const from = count ? (page - 1) * limit + 1 : 0;
  const to = (page - 1) * limit + count;
  const canNext = total !== undefined ? page < pages : !!hasMore;
  if (!count && page === 1) return null;
  return (
    <nav className="pager" aria-label={c.pagesLabel}>
      <span className="pager-range">
        {count ? (total !== undefined ? fmt(c.rangeOf, { from: num(from), to: num(to), total: num(total) }) : fmt(c.range, { from: num(from), to: num(to) })) : c.noRowsOnPage}
      </span>
      <div className="pager-right">
        <div className="pager-pages">
          <button type="button" className="pager-btn" onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label={c.previousPage}>
            <IconChevron size={16} />
            <span>{c.prev}</span>
          </button>
          {total !== undefined &&
            pageList(page, pages).map((p, i) =>
              p === "…" ? (
                <span key={`e${i}`} className="pager-gap" aria-hidden="true">…</span>
              ) : (
                <button key={p} type="button" className={`pager-num ${p === page ? "current" : ""}`} onClick={() => onPage(p)}
                  aria-current={p === page ? "page" : undefined} aria-label={fmt(c.pageN, { n: p })}>
                  {p}
                </button>
              ),
            )}
          <button type="button" className="pager-btn next" onClick={() => onPage(page + 1)} disabled={!canNext} aria-label={c.nextPage}>
            <span>{c.next}</span>
            <IconChevron size={16} />
          </button>
        </div>
        <label className="pager-size">
          <span>{c.rows}</span>
          <select value={limit} onChange={(e) => onLimit(Number(e.target.value))} aria-label={label ?? c.rowsPerPage}>
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
    </nav>
  );
}
